import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AccessRights from "../../../../configs/constants/accessRights";
import {
  getSlaConfigs,
  updateSlaConfig,
  getGlobalSlaConfigs,
  updateGlobalSlaConfig,
  getBusinessHours,
  updateBusinessHours,
  getHolidays,
  createHoliday,
  updateHoliday,
  deleteHoliday,
  getAutoReplies,
  updateAutoReply,
} from "../../redux/action";
import SlaSettingsPage from "./slaSettings.page";

export default function SlaSettingsContainer() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const canView = useSelector((state) => state.session.accessRights.includes(AccessRights.SLA_SETTINGS_VIEW));
  const canEdit = useSelector((state) => state.session.accessRights.includes(AccessRights.SLA_SETTINGS_EDIT));

  useEffect(() => {
    if (!canView) {
      navigate("/admin");
    }
  }, [canView]);

  const slaConfigs = useSelector((state) => state.support.slaConfigs);
  const globalSlaConfigs = useSelector((state) => state.support.globalSlaConfigs);
  const businessHours = useSelector((state) => state.support.businessHours);
  const holidays = useSelector((state) => state.support.holidays);
  const autoReplies = useSelector((state) => state.support.autoReplies);
  const [editingAutoReplyContext, setEditingAutoReplyContext] = useState(null);
  const [autoReplyDraft, setAutoReplyDraft] = useState({ message: "", is_active: true });
  const [editingPriority, setEditingPriority] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [editingGlobalKey, setEditingGlobalKey] = useState(null);
  const [editGlobalValues, setEditGlobalValues] = useState({});
  const [editingBusinessHours, setEditingBusinessHours] = useState(false);
  const [bhDraft, setBhDraft] = useState(null);
  const [holidayDialog, setHolidayDialog] = useState(null);

  useEffect(() => {
    dispatch(getSlaConfigs());
    dispatch(getGlobalSlaConfigs());
    dispatch(getBusinessHours());
    dispatch(getHolidays());
    dispatch(getAutoReplies());
  }, [dispatch]);

  const handleEditAutoReply = (config) => {
    setEditingAutoReplyContext(config.context);
    setAutoReplyDraft({
      message: config.message || "",
      is_active: !!config.is_active,
    });
  };

  const handleCancelAutoReply = () => {
    setEditingAutoReplyContext(null);
    setAutoReplyDraft({ message: "", is_active: true });
  };

  const handleChangeAutoReply = (field, value) => {
    setAutoReplyDraft((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveAutoReply = () => {
    if (!editingAutoReplyContext) return;
    dispatch(
      updateAutoReply({
        context: editingAutoReplyContext,
        message: autoReplyDraft.message,
        isActive: !!autoReplyDraft.is_active,
      })
    ).then(() => {
      setEditingAutoReplyContext(null);
      setAutoReplyDraft({ message: "", is_active: true });
    });
  };

  const handleEdit = (config) => {
    setEditingPriority(config.priority);
    setEditValues({
      response_hours: config.response_hours,
      response_hours_type: config.response_hours_type || "business",
      resolution_hours: config.resolution_hours,
      resolution_hours_type: config.resolution_hours_type || "business",
    });
  };

  const handleCancel = () => {
    setEditingPriority(null);
    setEditValues({});
  };

  const handleSave = () => {
    dispatch(
      updateSlaConfig({
        priority: editingPriority,
        responseHours: parseInt(editValues.response_hours, 10),
        responseHoursType: editValues.response_hours_type,
        resolutionHours: parseInt(editValues.resolution_hours, 10),
        resolutionHoursType: editValues.resolution_hours_type,
      })
    ).then(() => {
      setEditingPriority(null);
      setEditValues({});
    });
  };

  const handleChange = (field, value) => {
    setEditValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleEditGlobal = (config) => {
    setEditingGlobalKey(config.key);
    setEditGlobalValues({
      value: config.value,
      hours_type: config.hours_type || "business",
    });
  };

  const handleCancelGlobal = () => {
    setEditingGlobalKey(null);
    setEditGlobalValues({});
  };

  const handleSaveGlobal = () => {
    dispatch(
      updateGlobalSlaConfig({
        key: editingGlobalKey,
        value: parseInt(editGlobalValues.value, 10),
        hoursType: editGlobalValues.hours_type,
      })
    ).then(() => {
      setEditingGlobalKey(null);
      setEditGlobalValues({});
    });
  };

  const handleChangeGlobal = (field, value) => {
    setEditGlobalValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleEditBusinessHours = () => {
    if (!businessHours) return;
    setBhDraft({ ...businessHours });
    setEditingBusinessHours(true);
  };

  const handleCancelBusinessHours = () => {
    setBhDraft(null);
    setEditingBusinessHours(false);
  };

  const handleSaveBusinessHours = () => {
    if (!bhDraft) return;
    const payload = { timezone: bhDraft.timezone };
    ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"].forEach((day) => {
      payload[`${day}_enabled`] = !!bhDraft[`${day}_enabled`];
      payload[`${day}_start`] = (bhDraft[`${day}_start`] || "09:00:00").slice(0, 5);
      payload[`${day}_end`] = (bhDraft[`${day}_end`] || "18:00:00").slice(0, 5);
    });
    dispatch(updateBusinessHours(payload)).then(() => {
      setBhDraft(null);
      setEditingBusinessHours(false);
    });
  };

  const handleChangeBusinessHours = (field, value) => {
    setBhDraft((prev) => ({ ...prev, [field]: value }));
  };

  const handleOpenAddHoliday = () => {
    setHolidayDialog({
      mode: "add",
      id: null,
      name: "",
      start_date: new Date().toISOString().slice(0, 10),
      end_date: new Date().toISOString().slice(0, 10),
      is_active: true,
    });
  };

  const handleOpenEditHoliday = (holiday) => {
    setHolidayDialog({
      mode: "edit",
      id: holiday.id,
      name: holiday.name,
      start_date: (holiday.start_date || "").slice(0, 10),
      end_date: (holiday.end_date || "").slice(0, 10),
      is_active: !!holiday.is_active,
    });
  };

  const handleCloseHolidayDialog = () => setHolidayDialog(null);

  const handleChangeHolidayField = (field, value) => {
    setHolidayDialog((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveHoliday = () => {
    if (!holidayDialog) return;
    const payload = {
      name: holidayDialog.name,
      startDate: holidayDialog.start_date,
      endDate: holidayDialog.end_date,
      isActive: holidayDialog.is_active,
    };
    const action = holidayDialog.mode === "add"
      ? createHoliday(payload)
      : updateHoliday({ id: holidayDialog.id, ...payload });
    dispatch(action).then(() => setHolidayDialog(null));
  };

  const handleDeleteHoliday = (holiday) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove holiday "${holiday.name}"?`)) return;
    dispatch(deleteHoliday({ id: holiday.id }));
  };

  return (
    <SlaSettingsPage
      slaConfigs={slaConfigs}
      globalSlaConfigs={globalSlaConfigs}
      businessHours={businessHours}
      holidays={holidays}
      holidayDialog={holidayDialog}
      editingPriority={editingPriority}
      editValues={editValues}
      editingGlobalKey={editingGlobalKey}
      editGlobalValues={editGlobalValues}
      editingBusinessHours={editingBusinessHours}
      bhDraft={bhDraft}
      canEdit={canEdit}
      onEdit={handleEdit}
      onCancel={handleCancel}
      onSave={handleSave}
      onChange={handleChange}
      onEditGlobal={handleEditGlobal}
      onCancelGlobal={handleCancelGlobal}
      onSaveGlobal={handleSaveGlobal}
      onChangeGlobal={handleChangeGlobal}
      onEditBusinessHours={handleEditBusinessHours}
      onCancelBusinessHours={handleCancelBusinessHours}
      onSaveBusinessHours={handleSaveBusinessHours}
      onChangeBusinessHours={handleChangeBusinessHours}
      onOpenAddHoliday={handleOpenAddHoliday}
      onOpenEditHoliday={handleOpenEditHoliday}
      onDeleteHoliday={handleDeleteHoliday}
      onCloseHolidayDialog={handleCloseHolidayDialog}
      onChangeHolidayField={handleChangeHolidayField}
      onSaveHoliday={handleSaveHoliday}
      autoReplies={autoReplies}
      editingAutoReplyContext={editingAutoReplyContext}
      autoReplyDraft={autoReplyDraft}
      onEditAutoReply={handleEditAutoReply}
      onCancelAutoReply={handleCancelAutoReply}
      onChangeAutoReply={handleChangeAutoReply}
      onSaveAutoReply={handleSaveAutoReply}
    />
  );
}
